import { useEffect } from "react";
import { useDataTable } from "../../../../shared/hooks/DataTable";
import { InvoiceSummaryService } from "../../InvoiceSummaryService";
import {
  ODataPeriod,
  OInvoiceLookUpField,
  OInvoiceSummaryField,
} from "../../../../shared/constants";
import type { InvoiceLookUpTableProps } from "./summaryDetailsTableConsts";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { FormattedCurrency } from "../../../../shared/component/FormattedCurrency";
import { IamColumn } from "../../../../shared/hooks/DataTableBasic";
import moment from "moment";

export const InvoiceLookUpTableDialog = ({
  setLookupData,
  filterData,
  onHide,
  open,
}: InvoiceLookUpTableProps) => {
  const InvoiceLLookUpColumns: IamColumn[] = [
    {
      field: OInvoiceLookUpField.INVOICE_SERIAL,
      header: "Ký hiệu",
      width: 120,
      sortable: false,
      matchMode: "contains",
    },
    {
      field: OInvoiceLookUpField.INVOICE_NUMBER,
      header: "Số hóa đơn",
      width: 120,
      sortable: false,
      matchMode: "contains",
    },
    {
      field: OInvoiceLookUpField.BUYER_TAX_CODE,
      header: "Mã số thuế",
      width: 120,
      sortable: false,
      matchMode: "contains",
    },
    {
      field: OInvoiceLookUpField.BUYER_BUDGET_UNIT_CODE,
      header: "Mã đơn vị quan hệ ngân sách",
      sortable: false,
      width: 190,
      matchMode: "contains",
    },
    {
      field: OInvoiceLookUpField.BUYER_NAME,
      header: "Tên đơn vị/Tên người mua",
      width: 250,
      sortable: false,
      matchMode: "contains",
    },
    {
      field: OInvoiceLookUpField.TOTAL_AMOUNT,
      header: "Thành tiền sau thuế",
      width: 150,
      sortable: false,
      dataType: "custom",
      matchMode: "contains",
      customCell: (item) => (
        <FormattedCurrency
          value={item[OInvoiceLookUpField.TOTAL_AMOUNT]}
          currency={filterData.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.DATE_SIGN,
      header: "Ngày ký",
      dataType: "date-time",
      sortable: false,
      width: 120,
    },
    {
      field: OInvoiceLookUpField.CREATE_TIME,
      header: "Ngày lập",
      width: 120,
      sortable: false,
      dataType: "date-time",
    },
    {
      field: OInvoiceLookUpField.CREATOR_NAME,
      header: "Người lập",
      width: 200,
      sortable: false,
      matchMode: "contains",
    },
    {
      field: OInvoiceLookUpField.EXCHANGE_RATE,
      header: "Tỷ giá",
      width: 120,
      sortable: false,
      dataType: "custom",
      matchMode: "contains",
      customCell: (item) => (
        <FormattedCurrency
          value={item[OInvoiceLookUpField.EXCHANGE_RATE]}
          currency={filterData.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.TOTAL_FEE,
      header: "Tổng tiền phí",
      width: 150,
      sortable: false,
      dataType: "custom",
      matchMode: "contains",
      customCell: (item) => (
        <FormattedCurrency
          value={item[OInvoiceLookUpField.TOTAL_FEE]}
          currency={filterData.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.TOTAL_DISCOUNT,
      header: "Tổng giảm trừ khác",
      width: 150,
      sortable: false,
      dataType: "custom",
      matchMode: "contains",
      customCell: (item) => (
        <FormattedCurrency
          value={item[OInvoiceLookUpField.TOTAL_DISCOUNT]}
          currency={filterData.currencyCode}
        />
      ),
    },
  ];
  const {
    render: renderDataTable,
    refreshLazyData,
    selectedItems,
  } = useDataTable({
    indexColumnWidth: 50,
    columns: InvoiceLLookUpColumns,
    selectionMode: "checkbox",
    hideCurrentPageReport: true,
    hideRowsPerPageDropdown: true,
    getList: async () => {
      if (
        !filterData.serial ||
        (filterData.dataPeriod !== ODataPeriod.DAY && !filterData.year)
      )
        return Promise.resolve({ items: [], totalCount: 0 });
      const getInvoiceLookupsParams: Record<string, any> = {
        dataPeriod: filterData.dataPeriod,

        currencyCode: filterData.currencyCode,
        invoiceSerial: filterData.serial,
      };
      if (filterData.dataPeriod !== ODataPeriod.DAY) {
        getInvoiceLookupsParams[OInvoiceSummaryField.MONTH_OR_QUARTER] =
          parseInt(filterData.monthOrQuarter);
        getInvoiceLookupsParams.year = parseInt(filterData.year);
      }
      if (filterData.dataPeriod === ODataPeriod.DAY) {
        getInvoiceLookupsParams[OInvoiceSummaryField.DATA_PERIOD_DATE] = moment(
          filterData.dataPeriodDate
        ).format("YYYY-MM-DD");
      }

      return InvoiceSummaryService.getInvoiceLookups(
        getInvoiceLookupsParams
      ).then((data) => ({ items: data, totalCount: data.length }));
    },
  });

  useEffect(() => {
    if (filterData && open) {
      refreshLazyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filterData]);

  const footer = (
    <div className='flex justify-content-center'>
      <Button
        label='Đóng'
        onClick={() => {
          onHide(false);
        }}
        className='p-button-outlined p-button-secondary p-button-sm mr-2'
      />
      <Button
        label='Lưu'
        className='p-button-outlined p-button-sm mr-2'
        onClick={() => {
          console.log(selectedItems);

          const lookups = Array.isArray(selectedItems)
            ? selectedItems
            : [selectedItems];

          setLookupData(lookups);
          onHide(false);
        }}
      />
    </div>
  );

  return (
    <Dialog
      style={{ maxWidth: "100%" }}
      visible={open}
      header='Chọn hóa đơn vào bảng tổng hợp'
      footer={footer}
      onHide={() => onHide(false)}
    >
      <div className='grid w-full'>
        <div className='col-12'>{renderDataTable()}</div>
        <label className='col-12'>
          <span className='required'>* </span>
          Để đảm bảo dung lượng khi truyền dữ liệu với CQT. Chỉ chọn tối đa 2000
          hóa đơn trong một bảng tổng hợp
        </label>
      </div>
    </Dialog>
  );
};
